/**
 * StaffMagicLinkConsume — /staff/magic-link
 * Consumes a one-time magic-link token from the URL and signs the
 * staff member into their scoped business dashboard.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function StaffMagicLinkConsume() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSessionFromMagicLink } = useAuth();
  const [state, setState] = useState({ status: "loading", message: "Signing you in…" });

  useEffect(() => {
    const token = params.get("token");
    const biz   = params.get("biz");
    if (!token) {
      setState({ status: "error", message: "Missing sign-in token." });
      return;
    }
    (async () => {
      try {
        const r = await http.post("/staff/magic-links/consume", { token, business_id: biz || undefined });
        const sp_token = r.data.sp_token || r.data.token;
        setSessionFromMagicLink(sp_token, {
          id: `staff-${r.data.staff_email}`,
          email: r.data.staff_email,
          name: `Staff · ${r.data.staff_email.split("@")[0]}`,
          role: "staff",
          business_id: r.data.business_id,
          business_name: r.data.business_name,
        });
        setState({ status: "success", message: `Signed in to ${r.data.business_name || "your business"}` });
        // Strip token from URL for safety
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => {
          const slug = r.data.business_slug;
          navigate(slug ? `/business/${slug}/edit` : "/dashboard", { replace: true });
        }, 900);
      } catch (e) {
        setState({ status: "error", message: e?.response?.data?.detail || "This sign-in link is invalid or expired." });
      }
    })();
  }, [params, navigate, setSessionFromMagicLink]);

  return (
    <div className="min-h-[60vh] grid place-items-center px-4" data-testid="staff-magic-link-consume">
      <div className="wofy-card !p-8 max-w-md w-full text-center">
        <h1 className="font-display text-2xl mb-4">Staff sign-in</h1>
        {state.status === "loading" && (
          <div>
            <Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60 mb-3" />
            <p className="opacity-80">{state.message}</p>
          </div>
        )}
        {state.status === "success" && (
          <div data-testid="magic-link-success">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-600 mb-3" />
            <p className="font-medium">{state.message}</p>
            <p className="text-sm opacity-70 mt-1">Redirecting to your dashboard…</p>
          </div>
        )}
        {state.status === "error" && (
          <div data-testid="magic-link-error">
            <AlertCircle className="w-10 h-10 mx-auto text-red-600 mb-3" />
            <p className="font-medium mb-1">Sign-in failed</p>
            <p className="text-sm opacity-80 mb-4">{state.message}</p>
            <Link to="/" className="inline-flex items-center gap-1 text-sm wofy-link">
              Go home <ArrowRight className="w-3 h-3" />
            </Link>
            <p className="text-xs opacity-60 mt-3">Ask your business owner to send a fresh sign-in link.</p>
          </div>
        )}
      </div>
    </div>
  );
}
